import { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, Users as UsersIcon, BarChart3, UserCog, Ban, LogIn, Plus, RefreshCw, Trash2, RotateCcw, Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ContextualHelp, HelpTip } from '@/components/ContextualHelp';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  super_user: 'bg-purple-100 text-purple-800 border-purple-200',
  admin: 'bg-red-100 text-red-700 border-red-200',
  analyst: 'bg-blue-100 text-blue-700 border-blue-200',
  viewer: 'bg-slate-100 text-slate-600 border-slate-200',
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800 border-green-200',
  suspended: 'bg-amber-100 text-amber-800 border-amber-200',
  archived: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const SuperAdmin = ({ token, onImpersonate }) => {
  const [tab, setTab] = useState('organizations');
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const hdrs = useMemo(() => (
    { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  ), [token]);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/super/organizations`, { headers: hdrs });
      if (!r.ok) throw new Error('Failed to load organizations');
      const d = await r.json();
      setOrgs(d.organizations || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [hdrs]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const q = userSearch ? `?q=${encodeURIComponent(userSearch)}` : '';
      const r = await fetch(`${API}/api/super/users${q}`, { headers: hdrs });
      if (!r.ok) throw new Error('Failed to load users');
      const d = await r.json();
      setUsers(d.users || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [hdrs, userSearch]);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/super/usage`, { headers: hdrs });
      if (!r.ok) throw new Error('Failed to load usage');
      const d = await r.json();
      setUsage(d);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [hdrs]);

  useEffect(() => {
    if (tab === 'organizations') loadOrgs();
    if (tab === 'users') loadUsers();
    if (tab === 'usage') loadUsage();
  }, [tab, loadOrgs, loadUsers, loadUsage]);

  const changeRole = async (user_id, role) => {
    const r = await fetch(`${API}/api/super/users/${user_id}/role`,
      { method: 'PUT', headers: hdrs, body: JSON.stringify({ role }) });
    if (!r.ok) { toast.error((await r.json()).detail || 'Failed'); return; }
    toast.success(`Role updated to ${role}`);
    loadUsers();
  };

  const toggleSuspend = async (user_id, suspended) => {
    const r = await fetch(`${API}/api/super/users/${user_id}/suspend?suspended=${suspended}`,
      { method: 'PUT', headers: hdrs });
    if (!r.ok) { toast.error((await r.json()).detail || 'Failed'); return; }
    toast.success(suspended ? 'User suspended' : 'User reinstated');
    loadUsers();
  };

  const impersonate = async (user_id, email) => {
    if (!window.confirm(`Impersonate ${email}?\n\nAll actions you take will be attributed to you in the audit log. Session lasts 30 minutes.`)) return;
    const r = await fetch(`${API}/api/super/impersonate/${user_id}`,
      { method: 'POST', headers: hdrs });
    if (!r.ok) { toast.error((await r.json()).detail || 'Failed to impersonate'); return; }
    const d = await r.json();
    onImpersonate?.(d);
  };

  const [resending, setResending] = useState({});  // {user_id: true}
  const resendInvite = async (u) => {
    if (!window.confirm(
      `Reset ${u.full_name || u.email}'s password and email them a new invite?\n\n` +
      `Their current password will stop working immediately. They'll be forced to change the new one on first login.`)) return;
    setResending(prev => ({ ...prev, [u.id]: true }));
    try {
      const r = await fetch(`${API}/api/super/users/${u.id}/resend-invite`,
        { method: 'POST', headers: hdrs });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Failed to resend invite'); return; }
      if (d.temp_password_fallback) {
        toast.warning(
          `Password reset for ${u.email} but email delivery failed. ` +
          `Temp password: ${d.temp_password_fallback}`,
          { duration: 30000 }
        );
      } else if (d.email_delivered) {
        toast.success(`New invite email sent to ${u.email}`);
      } else {
        toast.success(`Password reset for ${u.email}. Share the new sign-in details securely.`);
      }
    } catch (e) {
      toast.error('Failed to resend invite');
    } finally {
      setResending(prev => {
        const next = { ...prev };
        delete next[u.id];
        return next;
      });
    }
  };

  const archiveOrg = async (org_id, name) => {
    if (!window.confirm(`Archive "${name}"?\n\nThis will suspend all members. Data is retained but no one can log in.`)) return;
    const r = await fetch(`${API}/api/super/organizations/${org_id}`,
      { method: 'DELETE', headers: hdrs });
    if (!r.ok) { toast.error((await r.json()).detail || 'Failed'); return; }
    toast.success('Organization archived');
    loadOrgs();
  };

  const reinstateOrg = async (org_id, name) => {
    if (!window.confirm(`Reinstate "${name}"?\n\nMembers suspended by the archive will regain access. Users individually suspended by an admin stay suspended.`)) return;
    const r = await fetch(`${API}/api/super/organizations/${org_id}`,
      { method: 'PUT', headers: hdrs, body: JSON.stringify({ status: 'active' }) });
    if (!r.ok) { toast.error((await r.json()).detail || 'Failed'); return; }
    toast.success(`"${name}" reinstated`);
    loadOrgs();
  };

  const changePlan = async (org, newPlan) => {
    if (newPlan === org.plan) return;
    const r = await fetch(`${API}/api/super/organizations/${org.id}`,
      { method: 'PUT', headers: hdrs, body: JSON.stringify({ plan: newPlan }) });
    if (!r.ok) { toast.error((await r.json()).detail || 'Failed'); return; }
    toast.success(`"${org.name}" moved to ${newPlan}`);
    loadOrgs();
  };

  const changeSeatLimit = async (org, newLimit) => {
    const n = Number(newLimit);
    if (!Number.isFinite(n) || n < 1 || n > 10000) {
      toast.error('Seat limit must be between 1 and 10,000');
      return;
    }
    if (n === Number(org.seat_limit)) return;
    const active = Number(org.active_member_count ?? org.member_count ?? 0);
    if (n < active) {
      if (!window.confirm(
        `"${org.name}" currently has ${active} active members. Setting the ` +
        `seat limit to ${n} will block new invites until enough seats are ` +
        `freed up. Continue?`)) return;
    }
    const r = await fetch(`${API}/api/super/organizations/${org.id}`,
      { method: 'PUT', headers: hdrs, body: JSON.stringify({ seat_limit: n }) });
    if (!r.ok) { toast.error((await r.json()).detail || 'Failed'); return; }
    toast.success(`"${org.name}" seat limit set to ${n}`);
    loadOrgs();
  };

  return (
    <div data-testid="super-admin-page" className="space-y-6">
      <ContextualHelp guideId="platform-admin" position="br" />
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserCog className="w-6 h-6 text-purple-700" />
            </div>
            <h1 className="text-3xl font-bold text-[#0F172A]">Platform Admin</h1>
            <Badge className={ROLE_COLORS.super_user}>Super User</Badge>
            <HelpTip
              testId="page-header"
              text="You have platform-wide access: manage tenants, assign roles across organizations, monitor usage, and impersonate users for support. Every action is audit-logged."
            />
          </div>
          <p className="mt-2 text-slate-500 text-sm">
            Manage tenants, users, and platform-wide usage.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger
            value="organizations"
            data-testid="super-tab-orgs"
            className="data-[state=active]:bg-[#0891b2] data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            <Building2 className="w-4 h-4 mr-2" />Organizations
          </TabsTrigger>
          <TabsTrigger
            value="users"
            data-testid="super-tab-users"
            className="data-[state=active]:bg-[#0891b2] data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            <UsersIcon className="w-4 h-4 mr-2" />Users
          </TabsTrigger>
          <TabsTrigger
            value="usage"
            data-testid="super-tab-usage"
            className="data-[state=active]:bg-[#0891b2] data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            <BarChart3 className="w-4 h-4 mr-2" />Usage
          </TabsTrigger>
        </TabsList>

        {/* ORGANIZATIONS */}
        <TabsContent value="organizations" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-slate-500 flex items-center gap-2">
              {orgs.length} organization{orgs.length !== 1 && 's'}
              <HelpTip
                testId="orgs-tab"
                text="Each organization is a separate tenant. Data between tenants stays isolated. Create a new tenant to onboard a customer or team, or archive one to lock everyone out while retaining their data."
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadOrgs} data-testid="super-refresh-orgs">
                <RefreshCw className="w-4 h-4 mr-2" />Refresh
              </Button>
              <Button size="sm" onClick={() => setShowCreate(true)} data-testid="super-new-org-btn">
                <Plus className="w-4 h-4 mr-2" />New Organization
              </Button>
              <HelpTip
                testId="new-org-btn"
                side="left"
                text="Creates a new tenant AND its first owner-admin user in one step. The owner receives an email with a temporary password. If email delivery fails, you'll see the temp password in a toast."
              />
            </div>
          </div>
          <div className="border rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="super-orgs-table">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold text-slate-700">Name</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      Plan
                      <HelpTip testId="org-plan-col" text="Starter / Growth / Enterprise / Legacy. 'Legacy' marks tenants auto-created during the migration for existing users." />
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      Status
                      <HelpTip testId="org-status-col" text="Active = normal. Suspended = temporarily blocked. Archived = permanently disabled but data retained." />
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-right">Members</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-right">
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      Seats
                      <HelpTip testId="org-seats-col" text="Active members / seat limit. When a tenant hits its seat limit, tenant admins can no longer invite new users until you raise the cap here or an existing user is removed." />
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      Last activity
                      <HelpTip testId="org-activity-col" text="Timestamp of the most recent upload session. Use this to spot tenants who signed up but never got started." />
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(o => (
                  <tr key={o.id} className="border-t border-slate-100" data-testid={`super-org-row-${o.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{o.name}</div>
                      <div className="text-xs text-slate-400 font-mono">{o.id.slice(0, 8)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={o.plan} onValueChange={(v) => changePlan(o, v)}>
                        <SelectTrigger className="h-8 w-32 border-slate-200"
                                       data-testid={`super-plan-${o.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="starter">Starter</SelectItem>
                          <SelectItem value="growth">Growth</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                          <SelectItem value="legacy">Legacy</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3"><Badge className={STATUS_COLORS[o.status] || ''}>{o.status}</Badge></td>
                    <td className="px-4 py-3 text-right tabular-nums">{o.member_count}</td>
                    <td className="px-4 py-3 text-right">
                      <SeatLimitCell org={o} onSave={(v) => changeSeatLimit(o, v)} />
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {o.last_activity_at ? new Date(o.last_activity_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {o.status === 'archived' ? (
                        <Button variant="ghost" size="sm" onClick={() => reinstateOrg(o.id, o.name)}
                                data-testid={`super-reinstate-org-${o.id}`}
                                title="Reinstate organization">
                          <RotateCcw className="w-4 h-4 text-emerald-600" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => archiveOrg(o.id, o.name)}
                                data-testid={`super-archive-org-${o.id}`}
                                title="Archive organization">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {orgs.length === 0 && !loading && (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No organizations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2 max-w-sm w-full">
              <Input placeholder="Search email or name…" value={userSearch}
                     onChange={(e) => setUserSearch(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && loadUsers()}
                     data-testid="super-user-search" />
              <HelpTip testId="user-search" text="Case-insensitive substring match across email and full name. Press Enter to search." />
            </div>
            <Button variant="outline" size="sm" onClick={loadUsers}>
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
          <div className="border rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="super-users-table">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold text-slate-700">User</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Organization</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      Role
                      <HelpTip testId="user-role-col" text="super_user = platform-wide access. admin = tenant admin. analyst = read/write within tenant. viewer = read-only. Changes apply immediately." />
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      Status
                      <HelpTip testId="user-status-col" text="Suspended users get a 403 on every API call — no login, no data access — until you reinstate them." />
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-right">
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      Actions
                      <HelpTip testId="user-actions-col" side="left" text="Login-as = impersonate for 30 min (banner shown, actions audit-logged). Envelope = reset password + re-email invite. Ban = toggle suspend." />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`super-user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{u.full_name || '—'}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{u.tenant_name || u.organization_name || '—'}</td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                        <SelectTrigger className="w-36 h-8" data-testid={`super-role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="super_user">super_user</SelectItem>
                          <SelectItem value="admin">admin</SelectItem>
                          <SelectItem value="analyst">analyst</SelectItem>
                          <SelectItem value="viewer">viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      {u.suspended
                        ? <Badge className="bg-amber-100 text-amber-800 border-amber-200">suspended</Badge>
                        : <Badge className="bg-green-100 text-green-800 border-green-200">active</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" title="Impersonate"
                                onClick={() => impersonate(u.id, u.email)}
                                data-testid={`super-impersonate-${u.id}`}>
                          <LogIn className="w-4 h-4 text-purple-600" />
                        </Button>
                        <Button variant="ghost" size="sm"
                                title={u.suspended
                                  ? 'Reinstate this user before resending an invite'
                                  : 'Reset password & re-email invite'}
                                disabled={!!resending[u.id] || u.suspended}
                                onClick={() => resendInvite(u)}
                                data-testid={`super-resend-invite-${u.id}`}>
                          {resending[u.id]
                            ? <Loader2 className="w-4 h-4 animate-spin text-cyan-600" />
                            : <Mail className={`w-4 h-4 ${u.suspended ? 'text-slate-300' : 'text-cyan-600'}`} />}
                        </Button>
                        <Button variant="ghost" size="sm" title={u.suspended ? 'Reinstate' : 'Suspend'}
                                onClick={() => toggleSuspend(u.id, !u.suspended)}
                                data-testid={`super-suspend-${u.id}`}>
                          <Ban className={`w-4 h-4 ${u.suspended ? 'text-green-600' : 'text-amber-600'}`} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && !loading && (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">No users match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* USAGE */}
        <TabsContent value="usage" className="mt-4">
          {usage ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard label="Organizations" value={usage.platform_totals.organizations_total} sub={`${usage.platform_totals.organizations_active} active`}
                          tip="Total number of tenants ever created (including archived)." />
                <StatCard label="Users" value={usage.platform_totals.users_total} sub={`${usage.platform_totals.users_with_mfa} with MFA`}
                          tip="All live users across all tenants. MFA-enabled count is a security-hygiene signal — higher is better." />
                <StatCard label="Uploads" value={usage.platform_totals.uploads_total.toLocaleString()} sub={`${usage.platform_totals.records_total.toLocaleString()} records`}
                          tip="Total upload sessions and the total spend records across the platform." />
                <StatCard label="Total Spend" value={`$${usage.platform_totals.spend_total_usd.toLocaleString()}`} sub="across all tenants"
                          tip="Sum of every spend record's amount across every tenant. USD-normalised at ingest. Use as a rough platform-scale metric." />
              </div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    Per-tenant usage
                    <HelpTip testId="per-tenant" text="One row per organization. Watch for tenants near their monthly_upload_limit and for ones with no activity in the last 30 days." />
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm" data-testid="super-usage-table">
                    <thead className="bg-slate-50 text-left">
                      <tr>
                        <th className="px-4 py-3 font-semibold text-slate-700">Tenant</th>
                        <th className="px-4 py-3 font-semibold text-slate-700">Plan</th>
                        <th className="px-4 py-3 font-semibold text-slate-700 text-right">Members</th>
                        <th className="px-4 py-3 font-semibold text-slate-700 text-right">Uploads</th>
                        <th className="px-4 py-3 font-semibold text-slate-700 text-right">Records</th>
                        <th className="px-4 py-3 font-semibold text-slate-700 text-right">Spend ($)</th>
                        <th className="px-4 py-3 font-semibold text-slate-700 text-right">
                          <span className="inline-flex items-center gap-1.5 justify-end">
                            LLM $ / mo
                            <HelpTip testId="llm-budget-col" side="left" text="AI spend this month vs. the tenant's monthly cap. Bar turns amber >60%, red >90%. Guards against Universal Key abuse." />
                          </span>
                        </th>
                        <th className="px-4 py-3 font-semibold text-slate-700">Last activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.per_tenant.map(t => {
                        const pct = t.llm_budget_pct_used || 0;
                        const barColor = pct >= 90 ? 'bg-red-500'
                                       : pct >= 60 ? 'bg-amber-500'
                                                   : 'bg-emerald-500';
                        return (
                        <tr key={t.id} className="border-t border-slate-100" data-testid={`super-usage-row-${t.id}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{t.name}</td>
                          <td className="px-4 py-3"><Badge variant="outline">{t.plan}</Badge></td>
                          <td className="px-4 py-3 text-right tabular-nums">{t.members}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{t.uploads}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{t.records.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">${t.total_spend_usd.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            <div className="text-xs text-slate-500">
                              ${(t.llm_month_cost_usd || 0).toFixed(2)} / ${(t.monthly_llm_budget_usd || 0).toFixed(0)}
                            </div>
                            <div className="mt-1 h-1.5 w-24 ml-auto rounded-full bg-slate-100 overflow-hidden">
                              <div className={`h-full ${barColor}`}
                                   style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {t.last_activity_at ? new Date(t.last_activity_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          ) : loading ? (
            <div className="text-center text-slate-400 py-10">Loading usage data…</div>
          ) : null}
        </TabsContent>
      </Tabs>

      <CreateOrgDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        token={token}
        onCreated={() => { setShowCreate(false); loadOrgs(); }}
      />
    </div>
  );
};

const StatCard = ({ label, value, sub, tip }) => (
  <Card>
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
        {label}
        {tip && <HelpTip testId={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} text={tip} />}
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const SeatLimitCell = ({ org, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(org.seat_limit ?? 5));
  const active = Number(org.active_member_count ?? org.member_count ?? 0);
  const limit = Number(org.seat_limit ?? 5);
  const atCap = active >= limit;

  useEffect(() => { setValue(String(org.seat_limit ?? 5)); }, [org.seat_limit]);

  const commit = async () => {
    setEditing(false);
    if (String(value) === String(org.seat_limit)) return;
    await onSave(value);
  };

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1">
        <Input
          type="number" min={1} max={10000}
          value={value}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setValue(String(org.seat_limit ?? 5)); setEditing(false); }
          }}
          className="h-8 w-20 text-right tabular-nums"
          data-testid={`super-seat-input-${org.id}`}
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium tabular-nums hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
        atCap ? 'text-red-600' : 'text-slate-700'
      }`}
      title={atCap
        ? `Seat limit reached — new invites are blocked until you raise this cap or a user is removed.`
        : `Click to change seat limit`}
      data-testid={`super-seat-cell-${org.id}`}
    >
      <span>{active}</span>
      <span className="text-slate-400">/</span>
      <span>{limit}</span>
    </button>
  );
};

const CreateOrgDialog = ({ open, onClose, token, onCreated }) => {
  const [form, setForm] = useState({
    name: '', plan: 'starter', owner_email: '', owner_full_name: '',
    owner_role: 'admin', monthly_upload_limit: 100, monthly_llm_budget_usd: 50,
    seat_limit: 5,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/super/organizations`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Failed to create org'); return; }
      if (d.email_delivered) {
        toast.success(`${form.name} created. Invite email sent to ${form.owner_email}.`);
      } else {
        toast.warning(
          `${form.name} created but email delivery failed.\nTemporary password: ${d.temp_password_fallback}`,
          { duration: 20000 }
        );
      }
      onCreated();
      setForm({ name: '', plan: 'starter', owner_email: '', owner_full_name: '',
                owner_role: 'admin', monthly_upload_limit: 100, monthly_llm_budget_usd: 50,
                seat_limit: 5 });
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="super-create-org-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Create New Organization
            <HelpTip testId="dialog-title" text="This creates a tenant AND its first owner-admin user in one shot. The owner gets an email with a temporary password and should change it + enable MFA on first login." />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="flex items-center gap-1.5">
              Organization name
              <HelpTip testId="field-name" text="Displayed everywhere — in the sidebar footer, in reports, and to end users. You can rename later without breaking anything." />
            </Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Acme Corp" data-testid="super-org-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1.5">
                Plan
                <HelpTip testId="field-plan" text="Starter = pilot / freemium. Growth = mid-tier paid. Enterprise = highest tier. Purely a label right now — enforcement of limits is off unless you set them below." />
              </Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger data-testid="super-org-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="growth">Growth</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                Monthly upload limit
                <HelpTip testId="field-upload-limit" text="Advisory soft-limit shown in the Usage tab. Set to a high number for enterprise, low for pilots. Enforcement is not automatic yet." />
              </Label>
              <Input type="number" value={form.monthly_upload_limit}
                     onChange={(e) => setForm({ ...form, monthly_upload_limit: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              Seat limit
              <HelpTip testId="field-seat-limit" text="Maximum number of active (non-suspended) users this tenant may have. Once reached, tenant admins get a 403 when inviting new users until you raise the cap here. Independent of billing plan." />
            </Label>
            <Input type="number" min={1} max={10000} value={form.seat_limit}
                   onChange={(e) => setForm({ ...form, seat_limit: Number(e.target.value) })}
                   data-testid="super-org-seat-limit" />
          </div>
          <hr />
          <div className="text-xs font-semibold text-slate-600 uppercase flex items-center gap-1.5">
            Owner account (will receive invite)
            <HelpTip testId="section-owner" text="This person gets an invite email with a temporary password. If email delivery fails you'll see the temp password in a toast so you can share it manually via a secure channel." />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              Owner email
              <HelpTip testId="field-owner-email" text="Must be unique across the platform — cannot already belong to any user. Real, deliverable address recommended so the invite email lands." />
            </Label>
            <Input type="email" value={form.owner_email}
                   onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                   placeholder="owner@company.com" data-testid="super-owner-email" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              Owner full name
              <HelpTip testId="field-owner-name" text="Used in the invite email greeting and displayed in the sidebar after login. Editable later by the user themselves." />
            </Label>
            <Input value={form.owner_full_name}
                   onChange={(e) => setForm({ ...form, owner_full_name: e.target.value })}
                   placeholder="Jane Doe" data-testid="super-owner-name" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              Initial role
              <HelpTip testId="field-owner-role" text="Recommended: Admin — the owner needs to manage their own users. Use Analyst for a hands-on user without user-mgmt rights. Viewer for a read-only owner (rare)." />
            </Label>
            <Select value={form.owner_role} onValueChange={(v) => setForm({ ...form, owner_role: v })}>
              <SelectTrigger data-testid="super-owner-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="analyst">Analyst</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !form.name || !form.owner_email || !form.owner_full_name}
                  data-testid="super-create-submit"
                  className="bg-[#0891b2] hover:bg-[#0e7490] text-white">
            {submitting ? 'Creating…' : 'Create & Invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
